import React, { useState, useEffect, useCallback, useRef } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getBookings, createBooking, updateBooking, deleteBooking, getServices, getCustomers } from './businessApi';
import Portal from '../../shared/Portal';

const STATUS_CONFIG = {
  pending:   { color: '#C8A84B', nl: 'Wachtend',   en: 'Pending',   icon: '⏳' },
  confirmed: { color: '#4AC87A', nl: 'Bevestigd',  en: 'Confirmed', icon: '✓' },
  cancelled: { color: '#C05050', nl: 'Geannuleerd',en: 'Cancelled', icon: '✗' },
  completed: { color: '#5B9CF6', nl: 'Afgerond',   en: 'Completed', icon: '★' },
  'no-show': { color: '#888888', nl: 'No-show',    en: 'No-show',   icon: '○' },
};

const SVC_ICONS = { wellness: '💚', fitness: '💪', coaching: '🧠', nutrition: '🥗', other: '⚡' };

const pad = (n) => String(n).padStart(2, '0');

const formatDate = (d, nl) =>
  new Date(d).toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { weekday: 'short', day: '2-digit', month: 'short' });

const formatTime = (d) => {
  const dt = new Date(d);
  return `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const isSameDay = (a, b) => {
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
};

const toDateStr = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

// Generate 30-minute time slots from 08:00 to 20:00
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 8; h < 20; h++) {
    slots.push(`${pad(h)}:00`);
    slots.push(`${pad(h)}:30`);
  }
  return slots;
})();

function StatusBadge({ status, nl }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, background: `${cfg.color}18`, border: `1px solid ${cfg.color}44`, borderRadius: 8, padding: '3px 8px', whiteSpace: 'nowrap' }}>
      {cfg.icon} {nl ? cfg.nl : cfg.en}
    </span>
  );
}

function Avatar({ name = '?', size = 36 }) {
  const colors = ['#4AC87A', '#5B9CF6', '#C8A84B', '#9B59B6', '#E67E22'];
  const bg = colors[Math.abs([...name].reduce((a, c) => a + c.charCodeAt(0), 0)) % colors.length];
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: `${bg}33`, border: `2px solid ${bg}66`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: bg, fontSize: size * 0.38, fontWeight: 800, flexShrink: 0 }}>
      {(name[0] || '?').toUpperCase()}
    </div>
  );
}

function BookingCard({ booking, nl, canEdit, onStatusChange, onDelete, large }) {
  const cfg = STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending;
  const svcIcon = SVC_ICONS[booking.service_category] || '📋';
  const bookedDate = booking.date || booking.requested_at;
  return (
    <div style={{ background: _T.bg3, borderRadius: large ? 18 : 14, padding: large ? 18 : 14, marginBottom: large ? 14 : 10, border: `1px solid ${_T.cardBorderBot}` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <Avatar name={booking.booker_name || booking.customer_name || '?'} size={large ? 44 : 36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ color: C.white, fontSize: large ? 15 : 14, fontWeight: 700 }}>{booking.booker_name || booking.customer_name || (nl ? 'Klant' : 'Customer')}</div>
            <StatusBadge status={booking.status} nl={nl} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <span style={{ fontSize: 14 }}>{svcIcon}</span>
            <span style={{ color: C.silver, fontSize: 13 }}>{booking.service_name || (nl ? 'Service' : 'Service')}</span>
            {booking.duration_min && <span style={{ color: C.silver2, fontSize: 11 }}>· {booking.duration_min}min</span>}
            {booking.price != null && <span style={{ color: _T.accent, fontSize: 12, fontWeight: 600 }}>· €{parseFloat(booking.price).toFixed(2)}</span>}
          </div>
          {bookedDate && (
            <div style={{ color: C.silver2, fontSize: 12, marginTop: 4 }}>
              📅 {bookedDate ? new Date(bookedDate).toLocaleString(nl ? 'nl-NL' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
            </div>
          )}
          {booking.notes && <div style={{ color: C.silver2, fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>"{booking.notes}"</div>}
        </div>
      </div>

      {/* Action buttons */}
      {canEdit && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {booking.status === 'pending' && (
            <button onClick={() => onStatusChange(booking.id, 'confirmed')}
              style={{ background: '#4AC87A20', border: '1px solid #4AC87A55', borderRadius: 8, color: '#4AC87A', fontSize: 11, fontWeight: 600, padding: '6px 10px', cursor: 'pointer' }}>
              ✓ {nl ? 'Bevestigen' : 'Confirm'}
            </button>
          )}
          {(booking.status === 'pending' || booking.status === 'confirmed') && (
            <>
              <button onClick={() => onStatusChange(booking.id, 'completed')}
                style={{ background: '#5B9CF620', border: '1px solid #5B9CF655', borderRadius: 8, color: '#5B9CF6', fontSize: 11, fontWeight: 600, padding: '6px 10px', cursor: 'pointer' }}>
                ★ {nl ? 'Afgerond' : 'Complete'}
              </button>
              <button onClick={() => onStatusChange(booking.id, 'no-show')}
                style={{ background: '#88888820', border: '1px solid #88888855', borderRadius: 8, color: '#888', fontSize: 11, fontWeight: 600, padding: '6px 10px', cursor: 'pointer' }}>
                ○ No-show
              </button>
              <button onClick={() => onStatusChange(booking.id, 'cancelled')}
                style={{ background: '#C0505015', border: '1px solid #C0505044', borderRadius: 8, color: '#C05050', fontSize: 11, fontWeight: 600, padding: '6px 10px', cursor: 'pointer' }}>
                ✗ {nl ? 'Annuleren' : 'Cancel'}
              </button>
            </>
          )}
          {large && (
            <button onClick={() => onDelete(booking.id)}
              style={{ background: '#C0505015', border: '1px solid #C0505044', borderRadius: 8, color: '#C05050', fontSize: 11, padding: '6px 10px', cursor: 'pointer', marginLeft: 'auto' }}>
              🗑
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── New Booking Modal ──────────────────────────────────────────────────────────
function NewBookingModal({ nl, businessId, services, customers, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedService, setSelectedService]   = useState(null);
  const [selectedDate, setSelectedDate]         = useState(toDateStr(new Date()));
  const [selectedTime, setSelectedTime]         = useState('');
  const [notes, setNotes]                       = useState('');
  const [custSearch, setCustSearch]             = useState('');
  const [saving, setSaving]                     = useState(false);
  const [addNewCust, setAddNewCust]             = useState(false);
  const [newCustName, setNewCustName]           = useState('');
  const [newCustEmail, setNewCustEmail]         = useState('');

  const filteredCusts = customers.filter(c => {
    const q = custSearch.toLowerCase();
    return !q || [(c.firstName || c.first_name || '') + ' ' + (c.lastName || c.last_name || ''), c.email, c.phone].some(v => (v||'').toLowerCase().includes(q));
  });

  const handleCreate = async () => {
    if (!selectedService || !selectedDate || !selectedTime) return;
    setSaving(true);
    const datetime = `${selectedDate}T${selectedTime}:00`;
    const payload = {
      service_id: selectedService.id,
      service_name: selectedService.name,
      date: datetime,
      requested_at: datetime,
      notes,
      status: 'confirmed',
      price: selectedService.price,
      duration_min: selectedService.duration_min,
    };
    if (selectedCustomer) {
      payload.customer_id = selectedCustomer.id;
      const name = [(selectedCustomer.firstName || selectedCustomer.first_name), (selectedCustomer.lastName || selectedCustomer.last_name)].filter(Boolean).join(' ') || selectedCustomer.name;
      payload.booker_name  = name;
      payload.booker_email = selectedCustomer.email;
      payload.booker_phone = selectedCustomer.phone;
    } else {
      payload.booker_name  = newCustName;
      payload.booker_email = newCustEmail;
    }
    await createBooking(businessId, payload).catch(() => {});
    setSaving(false);
    onCreated();
  };

  const steps = [
    nl ? 'Klant' : 'Customer',
    nl ? 'Service' : 'Service',
    nl ? 'Datum & tijd' : 'Date & time',
    nl ? 'Bevestigen' : 'Confirm',
  ];

  return (
    <Portal>
    <div style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: _T.bg2, borderRadius: '24px 24px 0 0', width: '100%', maxWidth: 600, maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 20, boxSizing: 'border-box' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ color: C.white, fontSize: 16, fontWeight: 700 }}>{nl ? 'Nieuwe boeking' : 'New Booking'}</div>
          <button onClick={onClose} style={{ background: _T.bg4, border: 'none', borderRadius: 8, color: C.silver, fontSize: 18, width: 32, height: 32, cursor: 'pointer' }}>×</button>
        </div>

        {/* Progress */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ width: '100%', height: 3, borderRadius: 2, background: i + 1 <= step ? _T.accent : _T.bg4 }} />
              <div style={{ fontSize: 10, color: i + 1 === step ? _T.accent : C.silver2, fontWeight: i + 1 === step ? 700 : 400 }}>{s}</div>
            </div>
          ))}
        </div>

        {/* Step content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>

          {/* Step 1: Customer */}
          {step === 1 && (
            <div>
              <div style={{ color: C.silver, fontSize: 12, marginBottom: 10 }}>{nl ? 'Zoek een bestaande klant of voeg een nieuwe toe' : 'Search an existing customer or add a new one'}</div>
              {!addNewCust ? (
                <>
                  <input value={custSearch} onChange={e => setCustSearch(e.target.value)}
                    placeholder={nl ? 'Zoek klant...' : 'Search customer...'}
                    style={{ width: '100%', boxSizing: 'border-box', background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, color: C.white, fontSize: 13, padding: '10px 14px', outline: 'none', marginBottom: 10 }} />
                  <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                    {filteredCusts.slice(0, 20).map(c => {
                      const name = [(c.firstName || c.first_name), (c.lastName || c.last_name)].filter(Boolean).join(' ') || c.name || c.email;
                      const isSelected = selectedCustomer?.id === c.id;
                      return (
                        <div key={c.id} onClick={() => setSelectedCustomer(isSelected ? null : c)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, marginBottom: 4, background: isSelected ? `${_T.accent}18` : _T.bg3, border: `1px solid ${isSelected ? _T.accent : _T.cardBorderBot}`, cursor: 'pointer' }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: `${_T.accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: _T.accent, fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
                            {(name[0] || '?').toUpperCase()}
                          </div>
                          <div>
                            <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{name}</div>
                            <div style={{ color: C.silver2, fontSize: 11 }}>{c.email}</div>
                          </div>
                          {isSelected && <span style={{ marginLeft: 'auto', color: _T.accent }}>✓</span>}
                        </div>
                      );
                    })}
                  </div>
                  <button onClick={() => setAddNewCust(true)}
                    style={{ width: '100%', marginTop: 8, background: _T.bg3, border: `1px dashed ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 12, padding: '10px', cursor: 'pointer' }}>
                    + {nl ? 'Nieuwe klant toevoegen' : 'Add new customer inline'}
                  </button>
                </>
              ) : (
                <div>
                  <input value={newCustName} onChange={e => setNewCustName(e.target.value)}
                    placeholder={nl ? 'Naam *' : 'Name *'}
                    style={{ display: 'block', width: '100%', boxSizing: 'border-box', background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 14px', outline: 'none', marginBottom: 8 }} />
                  <input type="email" value={newCustEmail} onChange={e => setNewCustEmail(e.target.value)}
                    placeholder={nl ? 'E-mail' : 'Email'}
                    style={{ display: 'block', width: '100%', boxSizing: 'border-box', background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 14px', outline: 'none', marginBottom: 8 }} />
                  <button onClick={() => setAddNewCust(false)}
                    style={{ background: 'transparent', border: 'none', color: _T.accent, fontSize: 12, cursor: 'pointer', padding: 0 }}>
                    ← {nl ? 'Terug naar zoeken' : 'Back to search'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Service */}
          {step === 2 && (
            <div>
              <div style={{ color: C.silver, fontSize: 12, marginBottom: 10 }}>{nl ? 'Kies een service' : 'Choose a service'}</div>
              {services.filter(s => s.active !== false && s.active !== 0).map(s => {
                const isSelected = selectedService?.id === s.id;
                return (
                  <div key={s.id} onClick={() => setSelectedService(isSelected ? null : s)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, marginBottom: 8, background: isSelected ? `${_T.accent}18` : _T.bg3, border: `1px solid ${isSelected ? _T.accent : _T.cardBorderBot}`, cursor: 'pointer' }}>
                    <span style={{ fontSize: 22 }}>{SVC_ICONS[s.category] || '📋'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: C.white, fontSize: 14, fontWeight: 600 }}>{s.name}</div>
                      <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>⏱ {s.duration_min}min {s.price != null ? `· €${parseFloat(s.price).toFixed(2)}` : ''}</div>
                    </div>
                    {isSelected && <span style={{ color: _T.accent, fontSize: 18 }}>✓</span>}
                  </div>
                );
              })}
              {services.filter(s => s.active !== false && s.active !== 0).length === 0 && (
                <div style={{ color: C.silver2, fontSize: 13, textAlign: 'center', padding: 32 }}>{nl ? 'Geen services beschikbaar' : 'No services available'}</div>
              )}
            </div>
          )}

          {/* Step 3: Date & Time */}
          {step === 3 && (
            <div>
              <label style={{ color: C.silver, fontSize: 11, display: 'block', marginBottom: 10 }}>
                {nl ? 'Datum' : 'Date'}
                <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                  style={{ display: 'block', marginTop: 4, width: '100%', boxSizing: 'border-box', background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 14px', outline: 'none' }} />
              </label>
              <div style={{ color: C.silver, fontSize: 11, marginBottom: 8 }}>{nl ? 'Tijdstip' : 'Time slot'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
                {TIME_SLOTS.map(t => {
                  const isSel = selectedTime === t;
                  return (
                    <button key={t} onClick={() => setSelectedTime(t)}
                      style={{ background: isSel ? `${_T.accent}25` : _T.bg3, border: `1.5px solid ${isSel ? _T.accent : _T.cardBorderBot}`, borderRadius: 8, color: isSel ? _T.accent : C.silver, fontSize: 13, fontWeight: isSel ? 700 : 400, padding: '9px 0', cursor: 'pointer' }}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Confirm */}
          {step === 4 && (
            <div>
              <div style={{ background: _T.bg3, borderRadius: 14, padding: 16, marginBottom: 14, border: `1px solid ${_T.cardBorderBot}` }}>
                <div style={{ color: C.white, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{nl ? 'Samenvatting' : 'Summary'}</div>
                {[
                  { label: nl ? 'Klant' : 'Customer', val: selectedCustomer ? [(selectedCustomer.firstName || selectedCustomer.first_name), (selectedCustomer.lastName || selectedCustomer.last_name)].filter(Boolean).join(' ') || selectedCustomer.email : newCustName || '—' },
                  { label: nl ? 'Service' : 'Service', val: selectedService?.name || '—' },
                  { label: nl ? 'Datum' : 'Date', val: selectedDate ? new Date(selectedDate + 'T00:00').toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { dateStyle: 'full' }) : '—' },
                  { label: nl ? 'Tijd' : 'Time', val: selectedTime || '—' },
                  { label: nl ? 'Duur' : 'Duration', val: selectedService?.duration_min ? `${selectedService.duration_min} min` : '—' },
                  { label: nl ? 'Prijs' : 'Price', val: selectedService?.price != null ? `€${parseFloat(selectedService.price).toFixed(2)}` : '—' },
                ].map((r, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < 5 ? `1px solid ${_T.cardBorderBot}` : 'none' }}>
                    <span style={{ color: C.silver, fontSize: 12 }}>{r.label}</span>
                    <span style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{r.val}</span>
                  </div>
                ))}
              </div>
              <label style={{ color: C.silver, fontSize: 11, display: 'block' }}>
                {nl ? 'Notities (optioneel)' : 'Notes (optional)'}
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                  placeholder={nl ? 'Extra informatie...' : 'Additional info...'}
                  style={{ display: 'block', marginTop: 4, width: '100%', boxSizing: 'border-box', background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 14px', outline: 'none', resize: 'vertical' }} />
              </label>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)}
              style={{ flex: 1, background: 'transparent', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, color: C.silver, fontSize: 13, padding: '12px', cursor: 'pointer' }}>
              ← {nl ? 'Vorige' : 'Back'}
            </button>
          )}
          {step < 4 ? (
            <button onClick={() => setStep(s => s + 1)}
              disabled={
                (step === 1 && !selectedCustomer && !newCustName.trim()) ||
                (step === 2 && !selectedService) ||
                (step === 3 && (!selectedDate || !selectedTime))
              }
              style={{ flex: 2, background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 13, fontWeight: 700, padding: '12px', cursor: 'pointer', opacity: ((step === 1 && (selectedCustomer || newCustName.trim())) || (step === 2 && selectedService) || (step === 3 && selectedDate && selectedTime)) ? 1 : 0.45 }}>
              {nl ? 'Volgende' : 'Next'} →
            </button>
          ) : (
            <button onClick={handleCreate} disabled={saving}
              style={{ flex: 2, background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 13, fontWeight: 700, padding: '12px', cursor: 'pointer' }}>
              {saving ? '...' : (nl ? 'Boeking aanmaken' : 'Create Booking')}
            </button>
          )}
        </div>
      </div>
    </div>
    </Portal>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function BusinessBookingsScreen({ lang, businessId, role, onNav }) {
  const nl      = lang === 'nl';
  const canEdit = role === 'owner' || role === 'admin';

  const [tab, setTab]             = useState('today');
  const [bookings, setBookings]   = useState([]);
  const [services, setServices]   = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showNew, setShowNew]     = useState(false);
  const [calDate, setCalDate]     = useState(toDateStr(new Date()));
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      getBookings(businessId),
      getServices(businessId),
      getCustomers(businessId),
    ]).then(([bkgs, svcs, custs]) => {
      setBookings(Array.isArray(bkgs) ? bkgs : []);
      setServices(Array.isArray(svcs) ? svcs : []);
      setCustomers(Array.isArray(custs) ? custs : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [businessId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleStatusChange = async (bid, status) => {
    await updateBooking(businessId, bid, { status }).catch(() => {});
    setBookings(prev => prev.map(b => b.id === bid ? { ...b, status } : b));
  };

  const handleDelete = async (bid) => {
    if (!window.confirm(nl ? 'Boeking verwijderen?' : 'Delete this booking?')) return;
    await deleteBooking(businessId, bid).catch(() => {});
    setBookings(prev => prev.filter(b => b.id !== bid));
  };

  // Filter bookings
  const applyFilters = (list) => list.filter(b => {
    const matchStatus  = statusFilter  === 'all' || b.status === statusFilter;
    const matchService = serviceFilter === 'all' || b.service_id === parseInt(serviceFilter);
    return matchStatus && matchService;
  });

  // Stats
  const today = toDateStr(new Date());
  const now   = new Date();
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay() + 1);
  const weekEnd   = new Date(weekStart); weekEnd.setDate(weekStart.getDate() + 6);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  const todayBkgs   = bookings.filter(b => isSameDay(b.date || b.requested_at, today));
  const weekBkgs    = bookings.filter(b => { const d = new Date(b.date || b.requested_at); return d >= weekStart && d <= weekEnd; });
  const pendingBkgs = bookings.filter(b => b.status === 'pending');
  const monthRevenue = bookings.filter(b => b.status === 'completed' && new Date(b.date || b.requested_at) >= monthStart).reduce((a, b) => a + (parseFloat(b.price) || 0), 0);

  // Calendar strip: 7 days starting from today
  const calDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(now.getDate() + i - 3);
    return toDateStr(d);
  });

  // Group bookings by date for list view
  const groupedByDate = {};
  const listBookings = applyFilters(bookings).sort((a, b) => new Date(a.date || a.requested_at) - new Date(b.date || b.requested_at));
  listBookings.forEach(b => {
    const dk = toDateStr(b.date || b.requested_at);
    if (!groupedByDate[dk]) groupedByDate[dk] = [];
    groupedByDate[dk].push(b);
  });

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: C.silver, fontSize: 13 }}>...</div>;

  return (
    <div style={{ paddingBottom: 32 }}>
      <style>{CSS}</style>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
        {[
          { label: nl ? 'Vandaag' : 'Today',     val: todayBkgs.length },
          { label: nl ? 'Deze week' : 'This week', val: weekBkgs.length },
          { label: nl ? 'Wachtend' : 'Pending',   val: pendingBkgs.length },
          { label: nl ? 'Omzet (mnd)' : 'Revenue (mo)', val: `€${monthRevenue.toFixed(0)}` },
        ].map((s, i) => (
          <div key={i} style={{ background: _T.bg3, borderRadius: 14, padding: '12px 14px', border: `1px solid ${_T.cardBorderBot}`, flex: 1, minWidth: 0, textAlign: 'center' }}>
            <div style={{ color: _T.accent, fontSize: 18, fontWeight: 800 }}>{s.val}</div>
            <div style={{ color: C.silver, fontSize: 10, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* New booking button + tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        <div style={{ display: 'flex', flex: 1, gap: 6 }}>
          {[
            ['today',    nl ? 'Vandaag' : 'Today'],
            ['calendar', nl ? 'Kalender' : 'Calendar'],
            ['list',     nl ? 'Lijst' : 'List'],
          ].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              style={{ flex: 1, padding: '9px 0', borderRadius: 10, border: `1.5px solid ${tab === k ? _T.accent : _T.cardBorderBot}`, background: tab === k ? `${_T.accent}18` : _T.bg3, color: tab === k ? _T.accent : C.silver, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
        {canEdit && (
          <button onClick={() => setShowNew(true)}
            style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 10, color: '#0a1220', fontSize: 12, fontWeight: 700, padding: '9px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + {nl ? 'Boeking' : 'Booking'}
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, overflowX: 'auto', paddingBottom: 4 }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 8, color: C.silver, fontSize: 12, padding: '6px 10px', outline: 'none' }}>
          <option value="all">{nl ? 'Alle statussen' : 'All statuses'}</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{nl ? v.nl : v.en}</option>)}
        </select>
        <select value={serviceFilter} onChange={e => setServiceFilter(e.target.value)}
          style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 8, color: C.silver, fontSize: 12, padding: '6px 10px', outline: 'none' }}>
          <option value="all">{nl ? 'Alle services' : 'All services'}</option>
          {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {/* ── Today view ── */}
      {tab === 'today' && (
        <>
          <div style={{ color: C.white, fontSize: 15, fontWeight: 700, marginBottom: 12 }}>
            📅 {nl ? 'Vandaag' : 'Today'} — {new Date().toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
          {applyFilters(todayBkgs).length === 0 ? (
            <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🗓️</div>
              <div style={{ fontSize: 14 }}>{nl ? 'Geen boekingen vandaag' : 'No bookings today'}</div>
            </div>
          ) : (
            applyFilters(todayBkgs)
              .sort((a, b) => new Date(a.date || a.requested_at) - new Date(b.date || b.requested_at))
              .map(b => (
                <BookingCard key={b.id} booking={b} nl={nl} canEdit={canEdit}
                  onStatusChange={handleStatusChange} onDelete={handleDelete} large />
              ))
          )}
        </>
      )}

      {/* ── Calendar view ── */}
      {tab === 'calendar' && (
        <>
          {/* 7-day strip */}
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 14 }}>
            {calDays.map(d => {
              const isToday = d === today;
              const isSel   = d === calDate;
              const dayBkgs = bookings.filter(b => isSameDay(b.date || b.requested_at, d));
              const dt      = new Date(d + 'T00:00');
              return (
                <button key={d} onClick={() => setCalDate(d)}
                  style={{ flexShrink: 0, width: 56, background: isSel ? `${_T.accent}20` : _T.bg3, border: `1.5px solid ${isSel ? _T.accent : isToday ? `${_T.accent}66` : _T.cardBorderBot}`, borderRadius: 12, padding: '8px 4px', cursor: 'pointer', textAlign: 'center' }}>
                  <div style={{ color: C.silver2, fontSize: 10, marginBottom: 3 }}>
                    {dt.toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { weekday: 'short' })}
                  </div>
                  <div style={{ color: isSel ? _T.accent : C.white, fontSize: 16, fontWeight: 800 }}>{dt.getDate()}</div>
                  {dayBkgs.length > 0 && (
                    <div style={{ background: _T.accent, borderRadius: 6, color: '#0a1220', fontSize: 10, fontWeight: 700, marginTop: 4, padding: '1px 0' }}>{dayBkgs.length}</div>
                  )}
                </button>
              );
            })}
          </div>
          <div style={{ color: C.white, fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
            {new Date(calDate + 'T00:00').toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
          {applyFilters(bookings.filter(b => isSameDay(b.date || b.requested_at, calDate))).length === 0 ? (
            <div style={{ textAlign: 'center', color: C.silver2, padding: 32, fontSize: 13 }}>{nl ? 'Geen boekingen op deze dag' : 'No bookings on this day'}</div>
          ) : (
            applyFilters(bookings.filter(b => isSameDay(b.date || b.requested_at, calDate)))
              .sort((a, b) => new Date(a.date || a.requested_at) - new Date(b.date || b.requested_at))
              .map(b => <BookingCard key={b.id} booking={b} nl={nl} canEdit={canEdit} onStatusChange={handleStatusChange} onDelete={handleDelete} />)
          )}
        </>
      )}

      {/* ── List view ── */}
      {tab === 'list' && (
        <>
          {Object.keys(groupedByDate).length === 0 ? (
            <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
              <div style={{ fontSize: 14 }}>{nl ? 'Geen boekingen gevonden' : 'No bookings found'}</div>
            </div>
          ) : (
            Object.entries(groupedByDate).map(([dateKey, dayBookings]) => (
              <div key={dateKey} style={{ marginBottom: 20 }}>
                <div style={{ color: C.silver, fontSize: 12, fontWeight: 700, letterSpacing: 1, marginBottom: 8, textTransform: 'uppercase' }}>
                  {new Date(dateKey + 'T00:00').toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                </div>
                {dayBookings
                  .sort((a, b) => new Date(a.date || a.requested_at) - new Date(b.date || b.requested_at))
                  .map(b => <BookingCard key={b.id} booking={b} nl={nl} canEdit={canEdit} onStatusChange={handleStatusChange} onDelete={handleDelete} />)}
              </div>
            ))
          )}
        </>
      )}

      {/* New booking modal */}
      {showNew && (
        <NewBookingModal
          nl={nl}
          businessId={businessId}
          services={services}
          customers={customers}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); loadAll(); }}
        />
      )}
    </div>
  );
}
